/**
 * Node Health — Admin-Only Mission Control
 * Dual-Node Infrastructure: Nighton (Mac Mini) + Balinas Mini
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBackendBaseUrl } from '../lib/apiBase';
import {
  Activity, Cpu, HardDrive, Wifi, WifiOff, RefreshCw,
  Server, Clock, AlertTriangle, CheckCircle, Database,
  Thermometer, Zap, Bell, BarChart3, Brain
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

// ─── Metric Card ─────────────────────────────────────────────
const MetricCard = ({ icon: Icon, label, value, unit, status, subtext }) => {
  const statusColors = {
    healthy: 'text-[#00ff88]',
    warning: 'text-[#ffa502]',
    critical: 'text-red-400',
    offline: 'text-gray-500',
  };
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 bg-gray-800 rounded-lg">
          <Icon className={`w-5 h-5 ${statusColors[status] || 'text-gray-400'}`} />
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
          status === 'healthy' ? 'bg-[#00ff88]/10 text-[#00ff88]' :
          status === 'warning' ? 'bg-[#ffa502]/10 text-[#ffa502]' :
          status === 'critical' ? 'bg-red-500/10 text-red-400' :
          'bg-gray-800 text-gray-500'
        }`}>
          {status}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">
        {value}<span className="text-sm text-gray-500 ml-1">{unit}</span>
      </p>
      {subtext && <p className="text-[10px] text-gray-600 mt-1">{subtext}</p>}
    </div>
  );
};

// ─── Node Card ───────────────────────────────────────────────
const NodeCard = ({ node, isOnline }) => {
  const isNighton = node.name.toLowerCase().includes('nighton');
  const accentColor = isNighton ? '#ffa502' : '#00ff88';
  const bgColor = isNighton ? 'bg-amber-500/5 border-amber-500/30' : 'bg-[#00ff88]/5 border-[#00ff88]/30';
  const offlineBg = 'bg-red-500/5 border-red-500/30';

  return (
    <div className={`border rounded-xl p-5 ${isOnline ? bgColor : offlineBg}`} data-testid={`node-card-${isNighton ? 'nighton' : 'balinas'}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {isNighton ? (
            <Brain className={`w-6 h-6 ${isOnline ? 'text-amber-400' : 'text-red-400'}`} />
          ) : (
            <Server className={`w-6 h-6 ${isOnline ? 'text-[#00ff88]' : 'text-red-400'}`} />
          )}
          <div>
            <h3 className="font-bold text-white">{node.name}</h3>
            <p className="text-xs text-gray-500">{node.type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOnline ? (
            <Wifi className="w-5 h-5" style={{ color: accentColor }} />
          ) : (
            <WifiOff className="w-5 h-5 text-red-400" />
          )}
          <span className={`text-xs font-medium ${isOnline ? '' : 'text-red-400'}`} style={isOnline ? { color: accentColor } : {}}>
            {isOnline ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Role description */}
      <p className="text-[10px] text-gray-500 mb-3 leading-relaxed">{node.role}</p>

      {/* CPU / RAM bars */}
      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <p className="text-xs text-gray-500">CPU Usage</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${node.cpu}%`,
                  backgroundColor: node.cpu > 80 ? '#f87171' : node.cpu > 60 ? '#ffa502' : accentColor,
                }}
              />
            </div>
            <span className="text-white font-medium text-xs">{node.cpu?.toFixed(1)}%</span>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500">RAM Usage</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${node.ram}%`,
                  backgroundColor: node.ram > 80 ? '#f87171' : node.ram > 60 ? '#ffa502' : accentColor,
                }}
              />
            </div>
            <span className="text-white font-medium text-xs">{node.ram?.toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Specialties */}
      {node.specialties?.length > 0 && (
        <div className="border-t border-gray-800 pt-3">
          <p className="text-[10px] text-gray-600 mb-2 uppercase tracking-wider">Powers</p>
          <div className="flex flex-wrap gap-1.5">
            {node.specialties.map((s, i) => (
              <span
                key={i}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor, border: `1px solid ${accentColor}30` }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── API Quota Card ──────────────────────────────────────────
const APIQuotaCard = ({
  api,
  used = 0,
  limit = 0,
  resetTime = '—',
  connected = true,
  usage_tracked: usageTracked = false,
  note,
}) => {
  const showBar = usageTracked && limit > 0;
  const showCount = usageTracked && limit === 0 && used > 0;
  const percentage = showBar ? (used / limit) * 100 : 0;
  const status = showBar ? (percentage > 90 ? 'critical' : percentage > 70 ? 'warning' : 'healthy') : 'neutral';

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="text-sm text-white font-medium">{api}</span>
        {showBar ? (
          <span className={`text-xs whitespace-nowrap ${
            status === 'critical' ? 'text-red-400' :
            status === 'warning' ? 'text-[#ffa502]' : 'text-[#00ff88]'
          }`}>
            {used.toLocaleString()} / {limit.toLocaleString()} CU
          </span>
        ) : showCount ? (
          <span className="text-xs whitespace-nowrap text-[#00ff88]">
            {used.toLocaleString()} reqs today
          </span>
        ) : (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
            connected ? 'text-[#00ff88] border-[#00ff88]/30 bg-[#00ff88]/10' : 'text-amber-400 border-amber-400/30 bg-amber-400/10'
          }`}>
            {connected ? '● Connected' : '○ Not connected'}
          </span>
        )}
      </div>
      {showBar ? (
        <>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                status === 'critical' ? 'bg-red-400' :
                status === 'warning' ? 'bg-[#ffa502]' : 'bg-[#00ff88]'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-gray-600 mt-1">Resets: {resetTime}</p>
        </>
      ) : showCount ? (
        <p className="text-[10px] text-gray-500 leading-relaxed">Public API — no daily cap • {resetTime !== '—' ? `Resets: ${resetTime}` : 'Live tracking'}</p>
      ) : (
        <p className="text-[10px] text-gray-500 leading-relaxed">{note || 'Connected — API usage is managed externally'}</p>
      )}
    </div>
  );
};

// ─── Main Node Health Component ──────────────────────────────
export default function NodeHealth() {
  const { token, isAuthenticated } = useAuth();
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchHealth = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/node-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }
      if (res.ok) {
        setHealthData(await res.json());
        setLastRefresh(new Date());
        setError(null);
      } else if (res.status !== 403) {
        setError('Failed to fetch node health data');
      }
    } catch (e) {
      console.error('Health fetch error:', e);
      setError('Failed to fetch node health data');
    }
    setLoading(false);
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchHealth]);

  const data = healthData;

  return (
    <div className="min-h-screen bg-[#0a0a12] p-4 md:p-6" data-testid="node-health">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Activity className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Node Health</h1>
              <p className="text-sm text-gray-400">Mission Control — Dual-Node Infrastructure</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">Last refresh: {lastRefresh.toLocaleTimeString()}</span>
            <button
              onClick={() => { setLoading(true); fetchHealth(); }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
          </div>
        </div>

        {error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-amber-400" />
          </div>
        ) : !data ? (
          <div className="bg-gray-900/40 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No node health payload — try refresh or verify admin access.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Preview Mode Banner */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-300 font-medium">Deployment Setup — For Your Team</p>
                <p className="text-xs text-amber-400/70 mt-1 leading-relaxed">
                  Balinas CPU/RAM metrics below are <strong className="text-amber-300">real live data</strong> from the host server via psutil.
                  Nighton metrics show <strong className="text-amber-300">offline</strong> until you point <code className="bg-amber-900/30 px-1 rounded">NIGHTON_URL</code> to your Mac Mini's IP address.
                </p>
                <div className="mt-3 space-y-1.5 text-xs text-amber-400/60">
                  <p>• <code className="bg-amber-900/30 px-1 rounded">NIGHTON_URL=http://&lt;MAC_MINI_IP&gt;:8002</code> — Set in <code className="bg-amber-900/30 px-1 rounded">backend/.env</code></p>
                  <p>• Nighton must expose <code className="bg-amber-900/30 px-1 rounded">GET /nighton/health</code> returning <code className="bg-amber-900/30 px-1 rounded">{"{"}"cpu_percent": 18.3, "ram_percent": 41.2, "uptime": "5 days"{"}"}</code></p>
                  <p>• Optional: add <code className="bg-amber-900/30 px-1 rounded">temperature_celsius</code> to the health response to display CPU temp</p>
                  <p>• Optional: add <code className="bg-amber-900/30 px-1 rounded">disk_percent</code> to show disk usage in the metrics grid</p>
                </div>
              </div>
            </div>

            {/* Heartbeat Status */}
            <div className={`border rounded-xl p-4 flex items-center justify-between ${
              data.heartbeat.status === 'connected'
                ? 'bg-[#00ff88]/5 border-[#00ff88]/30'
                : 'bg-red-500/5 border-red-500/30'
            }`} data-testid="heartbeat-status">
              <div className="flex items-center gap-4">
                {data.heartbeat.status === 'connected' ? (
                  <CheckCircle className="w-8 h-8 text-[#00ff88]" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                )}
                <div>
                  <h3 className="text-lg font-bold text-white">Node Heartbeat</h3>
                  <p className="text-sm text-gray-400">Nighton (Mac Mini) ↔ Balinas Connection</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${
                  data.heartbeat.status === 'connected' ? 'text-[#00ff88]' : 'text-red-400'
                }`}>
                  {data.heartbeat.latency}ms
                </p>
                <p className="text-xs text-gray-500">Latency</p>
              </div>
            </div>

            {/* Node Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {data.nodes.map((node, i) => (
                <NodeCard key={i} node={node} isOnline={node.status === 'online'} />
              ))}
            </div>

            {/* System Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard icon={Clock} label="System Uptime" value={data.system.uptime.split(',')[0]} unit="days" status="healthy" />
              <MetricCard icon={Zap} label="Total Requests" value={(data.system.totalRequests / 1000).toFixed(1)} unit="K" status="healthy" />
              <MetricCard icon={Activity} label="Avg Response" value={data.system.avgResponseTime} unit="ms" status={data.system.avgResponseTime > 500 ? 'warning' : 'healthy'} />
              <MetricCard icon={Database} label="DB Status" value="Connected" unit="" status="healthy" />
            </div>

            {/* Feature Tier Map */}
            <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <Brain className="w-4 h-4 text-amber-400" />
                Node Feature Routing
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <p className="text-xs font-bold text-amber-400 mb-2">Nighton Routes (Pro+)</p>
                  <div className="space-y-1">
                    {['Smart Yield Alerts', 'Advanced Backtesting', 'ML Opportunity Scoring', 'Monte Carlo Simulations'].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-3 bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-lg">
                  <p className="text-xs font-bold text-[#00ff88] mb-2">Balinas Routes (All Tiers)</p>
                  <div className="space-y-1">
                    {['DeFiLlama Yield Feeds', 'Live Gas Prices', 'GoPlus Security Scores', 'IL Calculator'].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-gray-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#00ff88]" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* API integrations — real connection status (no fabricated usage) */}
            <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#00ff88]" />
                External APIs
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {(data.apiQuotas || []).map((quota, i) => (
                  <APIQuotaCard key={i} {...quota} />
                ))}
              </div>
            </div>

            {/* Admin Notice */}
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-400 font-medium">Admin Access Only</p>
                <p className="text-xs text-gray-500 mt-1">
                  This dashboard is hidden from regular users and subscribers.
                  Hardware telemetry is never exposed to public APIs.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
